import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X, Info, Server } from 'lucide-react';
interface Tool {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}
interface ToolCategory {
  name: string;
  enabledCount: number;
  totalCount: number;
  expanded: boolean;
  tools: Tool[];
}
interface ToolSelectionProps {
  onClose: () => void;
  onAddMCPServer: () => void;
  hideCloseButton?: boolean;
}
export const ToolSelection = ({
  onClose,
  onAddMCPServer,
  hideCloseButton = false
}: ToolSelectionProps) => {
  const [categories, setCategories] = useState<ToolCategory[]>([{
    name: 'Gmail',
    enabledCount: 4,
    totalCount: 4,
    expanded: true,
    tools: [{
      id: 'gmail_send',
      name: 'gmail_send-gmail-message',
      description: 'Send an email message using Gmail.',
      enabled: true
    }, {
      id: 'gmail_create',
      name: 'gmail_create-gmail-draft',
      description: 'Create a draft email message using Gmail.',
      enabled: true
    }, {
      id: 'gmail_read',
      name: 'gmail_read-email',
      description: 'Read an email message using Gmail.',
      enabled: true
    }, {
      id: 'gmail_list',
      name: 'gmail_list-emails',
      description: 'List email messages using Gmail.',
      enabled: true
    }]
  }, {
    name: 'Google Workspace',
    enabledCount: 22,
    totalCount: 22,
    expanded: false,
    tools: Array(22).fill(null).map((_, i) => ({
      id: `gworkspace_${i}`,
      name: `google_workspace_tool_${i}`,
      description: `Google Workspace tool ${i} description`,
      enabled: true
    }))
  }, {
    name: 'Wix MCP',
    enabledCount: 8,
    totalCount: 8,
    expanded: false,
    tools: Array(8).fill(null).map((_, i) => ({
      id: `trino_${i}`,
      name: `trino_mcp_tool_${i}`,
      description: `Trino MCP tool ${i} description`,
      enabled: true
    }))
  }]);
  const totalEnabledTools = categories.reduce((sum, category) => sum + category.enabledCount, 0);
  const totalTools = categories.reduce((sum, category) => sum + category.totalCount, 0);
  const toggleCategory = (index: number) => {
    setCategories(prev => prev.map((category, i) => i === index ? {
      ...category,
      expanded: !category.expanded
    } : category));
  };
  const toggleTool = (categoryIndex: number, toolIndex: number) => {
    setCategories(prev => {
      const newCategories = [...prev];
      const category = {
        ...newCategories[categoryIndex]
      };
      const tools = [...category.tools];
      tools[toolIndex] = {
        ...tools[toolIndex],
        enabled: !tools[toolIndex].enabled
      };
      category.tools = tools;
      category.enabledCount = tools.filter(tool => tool.enabled).length;
      newCategories[categoryIndex] = category;
      return newCategories;
    });
  };
  const enableAllTools = (categoryIndex: number) => {
    setCategories(prev => {
      const newCategories = [...prev];
      const category = {
        ...newCategories[categoryIndex]
      };
      category.tools = category.tools.map(tool => ({
        ...tool,
        enabled: true
      }));
      category.enabledCount = category.totalCount;
      newCategories[categoryIndex] = category;
      return newCategories;
    });
  };
  const disableAllTools = (categoryIndex: number) => {
    setCategories(prev => {
      const newCategories = [...prev];
      const category = {
        ...newCategories[categoryIndex]
      };
      category.tools = category.tools.map(tool => ({
        ...tool,
        enabled: false
      }));
      category.enabledCount = 0;
      newCategories[categoryIndex] = category;
      return newCategories;
    });
  };
  return <div className="w-full" data-id="element-1602">
      <div className="p-3 flex items-center justify-between" data-id="element-1603">
        <div className="flex items-center" data-id="element-1604">
          <span className="text-sm text-gray-700" data-id="element-1605">Tool Selection</span>
          <span className="ml-2 text-xs text-gray-500" data-id="element-1606">
            {totalEnabledTools} of {totalTools} tools enabled
          </span>
        </div>
        {!hideCloseButton && <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1607">
            <X className="w-4 h-4" data-id="element-1608" />
          </button>}
      </div>
      <div className="p-2" data-id="element-1609">
        {categories.map((category, categoryIndex) => <div key={category.name} className="border border-gray-200 rounded-md mb-2" data-id="element-1610">
            <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => toggleCategory(categoryIndex)} data-id="element-1611">
              <div className="flex items-center" data-id="element-1612">
                <span className="text-sm font-medium text-gray-700" data-id="element-1613">
                  {category.name}
                </span>
                <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-md" data-id="element-1614">
                  {category.enabledCount}/{category.totalCount}
                </span>
              </div>
              <button className="text-gray-500" data-id="element-1615">
                {category.expanded ? <ChevronUp className="w-5 h-5" data-id="element-1616" /> : <ChevronDown className="w-5 h-5" data-id="element-1617" />}
              </button>
            </div>
            {category.expanded && <div className="px-4 pb-4 border-t border-gray-200 pt-2" data-id="element-1618">
                <div className="flex mb-3 space-x-2" data-id="element-1619">
                  <button className="px-3 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50" onClick={() => enableAllTools(categoryIndex)} data-id="element-1620">
                    Enable All
                  </button>
                  <button className="px-3 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50" onClick={() => disableAllTools(categoryIndex)} data-id="element-1621">
                    Disable All
                  </button>
                </div>
                <div className="space-y-3" data-id="element-1622">
                  {category.tools.map((tool, toolIndex) => <div key={tool.id} className="flex items-start" data-id="element-1623">
                      <div className={`w-5 h-5 mt-0.5 mr-3 border rounded-sm flex items-center justify-center ${tool.enabled ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`} onClick={() => toggleTool(categoryIndex, toolIndex)} data-id="element-1624">
                        {tool.enabled && <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1625">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1626" />
                          </svg>}
                      </div>
                      <div className="flex-1" data-id="element-1627">
                        <div className="text-xs text-gray-700" data-id="element-1628">{tool.name}</div>
                        <div className="text-xs text-gray-500" data-id="element-1629">
                          {tool.description}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>)}
        <div className="px-2 py-3" data-id="element-1630">
          <button className="w-full px-4 py-2 border border-gray-200 rounded-md text-xs font-medium flex items-center justify-center" onClick={onAddMCPServer} data-id="element-1631">
            <Server className="w-4 h-4 mr-2" data-id="element-1632" />
            <span data-id="element-1633">Add MCP Server</span>
          </button>
        </div>
      </div>
    </div>;
};